import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";

import ProductCard from "./components/ProductCard";
import { fetchProducts } from "../../lib/api";
import { useTemplate } from "../../context/TemplateContext";

const SORTS = [
  { key: "featured", label: "Featured" },
  { key: "price-asc", label: "Price: Low → High" },
  { key: "price-desc", label: "Price: High → Low" },
  { key: "rating", label: "Top Rated" },
]; // kept for reference; labels now come from cfg.copy.shop (Iter-77)

/**
 * SRC-styled shop grid. Filter chips across the top, sort dropdown +
 * price slider in a collapsible filters panel, 2/3/4-col product grid.
 * Categories come from the template config so each store type shows the
 * correct chips (clothing = tees/hoodies, skincare = serums/moisturizers…).
 */
export default function Shop() {
  const { config: cfg, basePath, slug } = useTemplate();
  const accent = cfg.brand?.accentColor || "#E60000";
  const shopCopy = useMemo(() => cfg.copy?.shop || {}, [cfg.copy]);
  const sorts = useMemo(() => ([
    { key: "featured", label: shopCopy.sortFeatured || "Featured" },
    { key: "price-asc", label: shopCopy.sortPriceAsc || "Price: Low → High" },
    { key: "price-desc", label: shopCopy.sortPriceDesc || "Price: High → Low" },
    { key: "rating", label: shopCopy.sortTopRated || "Top Rated" },
  ]), [shopCopy]);
  const categories = useMemo(
    () => [{ key: "", label: shopCopy.filterAllLabel || "All" }, ...(cfg.categories || []).map((c) => ({ key: c.key || c.slug, label: c.title || c.label }))],
    [cfg.categories, shopCopy.filterAllLabel],
  );

  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "";
  const search = params.get("search") || "";
  const [sort, setSort] = useState("featured");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [maxPrice, setMaxPrice] = useState(500);

  useEffect(() => {
    setLoading(true);
    fetchProducts({
      slug, category: category || undefined, search: search || undefined,
      fallback: cfg.demoProducts,
    }).then(setProducts).finally(() => setLoading(false));
  }, [category, search, slug, cfg.demoProducts]);

  const filtered = useMemo(() => {
    let list = products.filter((p) => Number(p.price) <= maxPrice);
    switch (sort) {
      case "price-asc":  list = [...list].sort((a, b) => a.price - b.price); break;
      case "price-desc": list = [...list].sort((a, b) => b.price - a.price); break;
      case "rating":     list = [...list].sort((a, b) => (b.rating || 0) - (a.rating || 0)); break;
      default:           list = [...list].sort((a, b) => Number(b.featured) - Number(a.featured));
    }
    return list;
  }, [products, sort, maxPrice]);

  const setCategory = (k) => {
    const next = new URLSearchParams(params);
    if (k) next.set("category", k); else next.delete("category");
    setParams(next, { replace: true });
  };

  return (
    <div data-testid="shop-page" className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-24">
      <div className="mb-10">
        <p className="text-[11px] tracking-[0.3em] uppercase text-neutral-500" data-testid="shop-eyebrow">{shopCopy.eyebrow || "— Shop"}</p>
        <h1 className="font-display text-5xl sm:text-6xl mt-3 leading-none" data-testid="shop-title">
          {category ? categories.find((c) => c.key === category)?.label || "Shop" : (shopCopy.title || "Shop all.")}
        </h1>
        {search && <p className="text-sm text-neutral-500 mt-3">{shopCopy.resultsPrefix || "Results for"} &ldquo;{search}&rdquo;</p>}
      </div>

      {/* Category chips + sort — all visible labels editable (Iter-77) */}
      <div data-testid="shop-toolbar">
      <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-3 mb-6">
        {categories.map((c) => (
          <button
            key={c.key || "all"}
            onClick={() => setCategory(c.key)}
            data-testid={`filter-cat-${c.key || "all"}`}
            className={`px-4 py-2 text-xs uppercase tracking-[0.2em] whitespace-nowrap border transition-colors ${
              (category || "") === c.key ? "bg-black text-white border-black" : "bg-white text-black border-neutral-300 hover:border-black"
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {/* Sort + Filters row */}
      <div className="flex items-center justify-between border-y border-neutral-200 py-3 mb-8">
        <button
          onClick={() => setFiltersOpen((v) => !v)}
          data-testid="filters-toggle"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em]"
        >
          <SlidersHorizontal className="w-4 h-4" strokeWidth={1.5} />
          {filtersOpen ? "Hide" : (shopCopy.filtersLabel || "Filters")}
        </button>
        <div className="flex items-center gap-3">
          <label className="text-xs uppercase tracking-[0.2em] text-neutral-500">{shopCopy.sortLabel || "Sort"}</label>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            data-testid="sort-select"
            className="text-xs uppercase tracking-[0.2em] border border-neutral-300 px-3 py-2 bg-white"
          >
            {sorts.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </div>
      </div>
      </div>

      {filtersOpen && (
        <div className="border border-neutral-200 p-6 mb-10 grid sm:grid-cols-2 gap-6">
          <div>
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs uppercase tracking-[0.2em]">Max price</p>
              <p className="text-xs font-heading">${maxPrice}</p>
            </div>
            <input
              type="range" min="10" max="500" value={maxPrice}
              onChange={(e) => setMaxPrice(Number(e.target.value))}
              data-testid="price-slider"
              className="w-full accent-black"
            />
          </div>
          <button onClick={() => { setMaxPrice(500); setCategory(""); }} className="self-end inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em]">
            <X className="w-3 h-3" strokeWidth={1.5} /> Reset
          </button>
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-10 sm:gap-x-6">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="aspect-[3/4] bg-neutral-100 animate-pulse" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-20 text-center" data-testid="shop-empty">
          <p className="font-display text-3xl">Nothing matches.</p>
          <p className="text-sm text-neutral-500 mt-2">Try clearing filters or checking another category.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-10 sm:gap-x-6">
          {filtered.map((p, i) => (
            <ProductCard key={p.id || p.slug} product={p} basePath={basePath} accent={accent} eager={i < 4} />
          ))}
        </div>
      )}
    </div>
  );
}
